import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Badge from '@amzn/awsui-components-core/badge';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Select from '@amzn/awsui-components-core/select';
import KeyValuePairs from '@amzn/awsui-components-core/key-value-pairs';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import { buildDatasetColumns } from 'src/components/star-grid/datasets/utils/datasetDraft';
import { reportFeaturesSummary } from 'src/components/star-grid/datasets/utils/datasetCapabilities';
import { ReviewGeneratedQuery } from 'src/components/star-grid/datasets/components/ReviewGeneratedQuery';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';

export const WizardReviewStep: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const columns = state.profile
    ? buildDatasetColumns(state.profile, { roles: state.roles, labels: state.labels, hierarchyChoices: state.hierarchyChoices })
    : [];
  const validation = state.saved?.validation;
  return (
    <div className={styles.body}>
      <p className={styles.help}>{text('wizardReviewHelp')}</p>
      <SpaceBetween size="m">
        <FormField label={text('wizardReviewName')}>
          <Input
            value={state.name}
            placeholder={text('wizardReviewNamePlaceholder')}
            ariaLabel={text('wizardReviewName')}
            data-testid="wizard-name"
            onChange={({ detail }) => state.setName(detail.value)}
          />
        </FormField>
        <FormField label={text('wizardReviewVisibility')} description={text('datasetVisibilityUpcoming')}>
          <Select
            disabled
            ariaLabel={text('wizardReviewVisibility')}
            data-testid="wizard-visibility"
            options={[{ value: 'global', label: text('wizardReviewVisibilityGlobal') }]}
            selectedOption={{ value: 'global', label: text('wizardReviewVisibilityGlobal') }}
          />
        </FormField>
        <Box>
          {text('wizardReviewColumns')}: {columns.length}
        </Box>
        <Box data-testid="wizard-review-features">
          {text('wizardFeatures')}: {reportFeaturesSummary(state.capabilities, text)}
        </Box>
        {state.appliedFixedFilters.length > 0 && (
          <Box data-testid="wizard-review-fixed-filters">
            {text('wizardFixedFilters')}:{' '}
            {state.appliedFixedFilters
              .map(
                (filter) =>
                  `${filter.column} ${
                    filter.op === 'between'
                      ? `${filter.values[0]}–${filter.values[1]}`
                      : `${filter.op === 'in' ? 'in' : '='} ${filter.values.join(', ')}`
                  }`
              )
              .join('; ')}
          </Box>
        )}
        {state.error && (
          <Alert type="error" data-testid="wizard-save-error">
            {state.error}
          </Alert>
        )}
        {state.saved && (
          <SpaceBetween size="xs">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <StatusIndicator type="success" data-testid="wizard-saved">
                {state.editing ? `${text('datasetSavedAsVersion')} ${state.saved.version}` : text('wizardReviewSaved')}
              </StatusIndicator>
              {state.published ? (
                <StatusIndicator type="success" data-testid="dataset-published">
                  {text('datasetPublished')}
                </StatusIndicator>
              ) : (
                <>
                  <Badge color="grey">{text('datasetDraftPill')}</Badge>
                  <Button data-testid="dataset-publish" loading={state.publishing} onClick={state.publish}>
                    {text('datasetPublish')}
                  </Button>
                </>
              )}
            </SpaceBetween>
            {validation && (
              <KeyValuePairs
                columns={1}
                items={[
                  { label: text('wizardReviewRowCount'), value: String(validation.row_count) },
                  { label: text('wizardReviewRowCountAfter'), value: String(validation.row_count_after_lookups) },
                  {
                    label: text('wizardReviewTotals'),
                    value: Object.entries(validation.totals)
                      .map(([measure, total]) => `${measure}: ${total}`)
                      .join(', ')
                  }
                ]}
              />
            )}
          </SpaceBetween>
        )}
        <ReviewGeneratedQuery state={state} text={text} />
      </SpaceBetween>
    </div>
  );
};
